import { Stepper } from "@shared/components/Stepper";
import { useFormCompletionTracker } from "@shared/hooks/useFormCompletionTracker";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import React, { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@shared/hooks/use-toast";
import { useCreateBulkRequest } from "@features/cx/disconnect/hooks";
import { SelectConsumersStep } from "./stepComponents/selectConsumers/SelectConsumersStep";
import { RequestDetailsStep } from "./stepComponents/requestDetails/RequestDetailsStep";
import { ReviewSubmitStep } from "./stepComponents/reviewSubmit/ReviewSubmitStep";
import { normalizeDate } from "@shared/utils/dateUtils";

const CreateBulkDisconnectRequest: React.FC = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const navigate = useNavigate();
  const storageKey = `bulk-disconnection-request-progress-${remoteUtilityId}`;
  const { mutate: submitBulkRequest, isPending } = useCreateBulkRequest();

  const { startTracking, logTimeUntilStep, stopTracking } =
    useFormCompletionTracker("Bulk Disconnection");

  const [selectedCount, setSelectedCount] = React.useState(0);

  // Function to sync count from localStorage
  const syncSelectedCount = useCallback(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const data = JSON.parse(raw);
        const count = data.stepData?.[0]?.selectedConsumers?.length || 0;
        setSelectedCount(count);
      }
    } catch (e) {
      console.error("Failed to sync selected count:", e);
    }
  }, [storageKey]);

  // Initial sync
  React.useEffect(() => {
    syncSelectedCount();
  }, [syncSelectedCount]);

  const handleComplete = useCallback(
    (stepData: any) => {
      // Collect all data from the stepper's internal state
      const selectionData = stepData[0] || {};
      const detailsData = stepData[1] || {};

      const selectedConsumers = selectionData.selectedConsumers || [];

      // Construct final payload matching backend requirements
      const finalPayload = {
        remoteUtilityId: Number(remoteUtilityId),
        requestType: "Disconnect Permanent",
        consumerIds: selectedConsumers.map((c: any) => Number(c.id)),
        reason: detailsData.reasonLabel || detailsData.reason,
        preferredDate: detailsData.scheduledDate
          ? normalizeDate(detailsData.scheduledDate)
          : null,
        preferredTimeSlot: Number(detailsData.timeSlot) || 0,
        additionalNotes: detailsData.consumerRemark || "",
        priority: detailsData.priority ?? 0,
        source: 0,
      };

      console.log("Submitting bulk disconnection request:", finalPayload);

      submitBulkRequest(finalPayload, {
        onSuccess: () => {
          stopTracking?.();
          navigate("/cx/disconnections/");
        },
      });
    },
    [navigate, remoteUtilityId, submitBulkRequest, stopTracking],
  );

  // Memoize the steps array to prevent infinite re-renders
  const steps = useMemo(
    () => [
      {
        title: "Select Consumers",
        slug: "select-consumers",
        component: (
          <SelectConsumersStep
            remoteUtilityId={Number(remoteUtilityId)}
            storageKey={storageKey}
            logTimeUntilStep={logTimeUntilStep}
            onSelectionChange={(count) => setSelectedCount(count)}
          />
        ),
        optional: false,
        infoTitle: "Select Consumers Help",
        infoDescription: `Select consumers for bulk disconnection. ${selectedCount} consumers selected.`,
      },
      {
        title: "Request Details",
        slug: "request-details",
        component: (
          <RequestDetailsStep
            remoteUtilityId={Number(remoteUtilityId)}
            storageKey={storageKey}
            logTimeUntilStep={logTimeUntilStep}
          />
        ),
        optional: false,
        infoTitle: "Request Details Help",
        infoDescription: `These details will apply to all ${selectedCount} selected consumers.`,
      },
      {
        title: "Review & Submit",
        slug: "review-submit",
        component: (
          <ReviewSubmitStep
            remoteUtilityId={Number(remoteUtilityId)}
            storageKey={storageKey}
            logTimeUntilStep={logTimeUntilStep}
            stopTracking={stopTracking}
          />
        ),
        optional: false,
        infoTitle: "Review & Submit Help",
        infoDescription:
          "Review all your bulk disconnection details before submitting the request.",
      },
    ],
    [remoteUtilityId, storageKey, logTimeUntilStep, stopTracking, selectedCount],
  );

  return (
    <div className="flex flex-col gap-5">
      <Stepper
        steps={steps}
        onStepChange={() => {
          syncSelectedCount();
        }}
        onStepSave={async (stepIndex, stepData, isDirty) => {
          // Storage is handled at component level or by Stepper internally
        }}
        enableApiSave={false}
        saveOnStepChange={false}
        trackDirtyState={false}
        autoSave={false}
        persistProgress={false}
        allowSkip={false}
        storageKey={storageKey}
        enableRouting={true}
        onValidation={undefined}
        onComplete={handleComplete}
        showDraftResume={false}
      />
    </div>
  );
};

export default CreateBulkDisconnectRequest;
